import classNames from "classnames";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import Star from "@components/ShopRatingToolTip/Star";
import { Text } from "@components/Text";
import Auth from "@hooks/useAuth";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import { useShopRatingToolTip_RecreateUserShopRatingMutation as useRecreateUserShopRatingMutation } from "./__generated__/mutations";
import { useShopRatingToolTip_UserShopRatingQuery as useUserShopRatingQuery } from "./__generated__/queries";
import styles from "./index.module.css";
import type { FC } from "react";

type Props = {
  shopUuid: string;
};

const ShopRatingToolTip: FC<Props> = ({ shopUuid }) => {
  const [isOpened, setIsOpened] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const toolTipButtonRef = useRef<HTMLButtonElement>(null);
  const { isFanstaUserRegisteredAndValidated } = Auth.useContainer();
  const router = useRouter();

  const { data, loading } = useUserShopRatingQuery({
    variables: { shopUuid },
    skip: !isFanstaUserRegisteredAndValidated,
  });
  const rating = data?.userShopRating?.rating || 0;

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (
        !(e.target instanceof HTMLInputElement && e.target?.name === "rate")
      ) {
        setIsOpened(false);
      }
    };
    // 星以外のクリック時にツールチップを閉じる
    document.addEventListener("click", listener);
    return () => {
      document.removeEventListener("click", listener);
    };
  }, []);

  const classes = isOpened
    ? [styles.box, styles.show]
    : [styles.box, styles.hidden];

  const [recreateUserShopRatingMutation] = useRecreateUserShopRatingMutation();

  const handleClick = async (newRating: number) => {
    if (isBusy) {
      return;
    }
    setIsBusy(true);
    setIsOpened(false); // APIレスポンス時間に依存しないように先に閉じる
    await recreateUserShopRatingMutation({
      variables: {
        shopUuid,
        rating: newRating,
      },
    });
    GTMEvent(GTMEvents.shopRating(shopUuid, newRating));

    // レーティング表示更新のため、サーバーサイドpropsをリフレッシュする
    // SEE: https://www.joshwcomeau.com/nextjs/refreshing-server-side-props/
    await router.replace(router.asPath);

    setIsBusy(false);
  };

  if (!isFanstaUserRegisteredAndValidated) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <button
        className={styles.button}
        type="button"
        onClick={(e) => {
          setIsOpened(!isOpened);
          e.stopPropagation();
        }}
        ref={toolTipButtonRef}
      >
        評価する
      </button>
      {!loading && (
        <div className={classNames(classes)}>
          <Text className={styles.text} size={12}>
            このお店の観戦体験はいかがでしたか？
          </Text>
          <div className={styles.rating}>
            {[1, 2, 3, 4, 5].map((v) => (
              <Star
                key={v}
                value={v}
                rating={rating}
                onClick={handleClick}
                disabled={isBusy}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopRatingToolTip;
